/**
 * Carrousel rotatif de pubs Mon Bureau Agricole + bouton "Je suis intéressé".
 * Affiché dans le Portail Client ETA pour créer le besoin et générer des leads
 * parrainés par l'ETA.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  X,
  Sparkles,
  Check,
  Loader2,
  PhoneCall,
  Mail,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MBAPubCarousel() {
  const [pubs, setPubs] = useState([]);
  const [thematiques, setThematiques] = useState([]);
  const [current, setCurrent] = useState(0);
  const [showInterestModal, setShowInterestModal] = useState(false);

  useEffect(() => {
    axios.get(`${API}/parrainage-eta/pubs`).then((r) => {
      setPubs(r.data.pubs || []);
      setThematiques(r.data.thematiques || []);
    });
  }, []);

  // Auto-rotation toutes les 7s
  useEffect(() => {
    if (pubs.length <= 1) return;
    const t = setInterval(() => {
      setCurrent((c) => (c + 1) % pubs.length);
    }, 7000);
    return () => clearInterval(t);
  }, [pubs.length]);

  if (!pubs.length) return null;

  const pub = pubs[current];

  return (
    <>
      <div
        className="rounded-2xl overflow-hidden shadow-xl my-8 transition-all duration-500"
        style={{
          background: `linear-gradient(135deg, ${pub.couleur}ee, ${pub.couleur}cc)`,
        }}
        data-testid="mba-pub-carousel"
      >
        <div className="p-8 md:p-10 text-white relative">
          {/* Background decorative */}
          <div className="absolute top-4 right-6 text-[120px] opacity-10 select-none">
            {pub.emoji}
          </div>

          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs uppercase tracking-[0.2em] opacity-80">
                Mon Bureau Agricole
              </span>
              <Sparkles className="w-3.5 h-3.5 opacity-70" />
            </div>

            <h3 className="text-2xl md:text-3xl font-bold leading-tight mb-3">
              {pub.titre}
            </h3>
            <p className="text-sm md:text-base opacity-95 mb-5 leading-relaxed max-w-2xl">
              {pub.accroche}
            </p>

            <div className="grid md:grid-cols-3 gap-2 mb-6 max-w-3xl">
              {(pub.benefices || []).map((b, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 text-xs md:text-sm opacity-90"
                >
                  <Check className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{b}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                data-testid="mba-pub-interesse-btn"
                onClick={() => setShowInterestModal(true)}
                className="px-5 py-2.5 rounded-xl bg-white text-slate-900 font-semibold text-sm hover:scale-105 transition-transform shadow-lg"
              >
                Je veux en savoir plus →
              </button>
              <button
                data-testid="mba-pub-rappel-btn"
                onClick={() => setShowInterestModal(true)}
                className="px-5 py-2.5 rounded-xl border border-white/30 text-white font-medium text-sm hover:bg-white/10 transition-all"
              >
                Je veux être rappelé
              </button>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-5 border-t border-white/20">
              <button
                onClick={() => setCurrent((c) => (c - 1 + pubs.length) % pubs.length)}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all"
                data-testid="mba-pub-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex gap-1.5">
                {pubs.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrent(i)}
                    className={`h-1.5 rounded-full transition-all ${
                      i === current ? 'w-8 bg-white' : 'w-1.5 bg-white/40'
                    }`}
                    aria-label={`Pub ${i + 1}`}
                  />
                ))}
              </div>
              <button
                onClick={() => setCurrent((c) => (c + 1) % pubs.length)}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all"
                data-testid="mba-pub-next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {showInterestModal && (
        <InteresseModal
          thematiques={thematiques}
          pubId={pub.id}
          onClose={() => setShowInterestModal(false)}
        />
      )}
    </>
  );
}

function InteresseModal({ thematiques, pubId, onClose }) {
  const [form, setForm] = useState({
    message: '',
    telephone: '',
    rappel_souhaite: false,
    thematiques: pubId ? [pubId] : [],
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const token = localStorage.getItem('portail_eta_token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const toggleTheme = (id) => {
    setForm((f) => ({
      ...f,
      thematiques: f.thematiques.includes(id)
        ? f.thematiques.filter((x) => x !== id)
        : [...f.thematiques, id],
    }));
  };

  const submit = async () => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/parrainage-eta/interesse`,
        form,
        auth
      );
      setDone(true);
      toast.success(r.data.message || 'Votre intérêt a bien été transmis !');
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || 'Erreur lors de l\'envoi'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-testid="mba-interesse-modal"
    >
      <div className="w-full max-w-xl bg-slate-900 rounded-3xl border border-white/10 shadow-2xl overflow-hidden">
        {done ? (
          <div className="p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 mx-auto flex items-center justify-center mb-4">
              <Check className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Merci !</h3>
            <p className="text-white/70 text-sm leading-relaxed">
              Votre intérêt a été transmis à votre ETA, qui reprendra contact
              avec vous rapidement pour vous présenter Mon Bureau Agricole.
            </p>
            <button
              onClick={onClose}
              className="mt-6 px-5 py-2.5 rounded-xl bg-lime-500 text-slate-900 font-semibold"
              data-testid="mba-interesse-close-done"
            >
              Fermer
            </button>
          </div>
        ) : (
          <>
            <div className="p-6 border-b border-white/10 flex items-start justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">
                  Je veux en savoir plus sur Mon Bureau Agricole
                </h3>
                <p className="text-xs text-white/60 mt-1">
                  Votre ETA sera informée de votre intérêt et reprendra contact
                  avec vous.
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-white/50 hover:text-white"
                data-testid="mba-interesse-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="text-xs text-white/60 mb-2 block">
                  Sujets qui vous intéressent
                </label>
                <div className="flex flex-wrap gap-2">
                  {thematiques.map((t) => {
                    const on = form.thematiques.includes(t.id);
                    return (
                      <button
                        key={t.id}
                        data-testid={`theme-${t.id}`}
                        type="button"
                        onClick={() => toggleTheme(t.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          on
                            ? 'bg-lime-400 text-slate-900'
                            : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10'
                        }`}
                      >
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="text-xs text-white/60 mb-2 block">
                  Téléphone (optionnel, pour être rappelé)
                </label>
                <input
                  data-testid="mba-interesse-tel"
                  value={form.telephone}
                  onChange={(e) =>
                    setForm({ ...form, telephone: e.target.value })
                  }
                  placeholder="06 12 34 56 78"
                  className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-lime-400/60"
                />
              </div>

              <div>
                <label className="text-xs text-white/60 mb-2 block">
                  Votre message (optionnel)
                </label>
                <textarea
                  data-testid="mba-interesse-msg"
                  rows={3}
                  value={form.message}
                  onChange={(e) =>
                    setForm({ ...form, message: e.target.value })
                  }
                  placeholder="Ex : Je voudrais voir comment ça marche pour la DRM…"
                  className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-lime-400/60"
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                <input
                  data-testid="mba-interesse-rappel"
                  type="checkbox"
                  checked={form.rappel_souhaite}
                  onChange={(e) =>
                    setForm({ ...form, rappel_souhaite: e.target.checked })
                  }
                  className="accent-lime-400 w-4 h-4"
                />
                Je souhaite être rappelé
              </label>

              <div className="pt-2 flex gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2.5 rounded-xl bg-white/5 text-white/70 text-sm"
                >
                  Annuler
                </button>
                <button
                  data-testid="mba-interesse-submit"
                  onClick={submit}
                  disabled={loading}
                  className="flex-1 px-5 py-2.5 rounded-xl bg-gradient-to-r from-lime-400 to-emerald-500 text-slate-900 font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                  Transmettre à mon ETA
                </button>
              </div>

              <p className="text-[11px] text-white/40 leading-relaxed pt-2">
                En cliquant, un email sera envoyé à votre ETA qui vous
                recontactera. Votre ETA reste votre interlocuteur principal.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
